import React, { FC } from "react";
import Image from "next/image";
import Box from "@mui/material/Box";

import { $public } from "@/services";

type Props = {
  src?: string;
  style?: React.CSSProperties;
  name: string;
  width?: number;
  height?: number;
  className?: string;
};

const CustomAvatar: FC<Props> = ({
  src,
  style,
  name,
  width = 40,
  height = 40,
  className,
}) => {
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const imageHasError = Boolean(src && failedSrc === src);

  const initials = name
    ?.trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return src && !imageHasError ? (
    <Image
      src={$public(src)}
      alt={`${name || "Service user"} profile`}
      width={width}
      height={height}
      onError={() => setFailedSrc(src)}
      className={className}
      style={{
        borderRadius: "50%",
        objectFit: "cover",
        ...style,
      }}
    />
  ) : (
    <Box
      role="img"
      aria-label={`${name || "Service user"} profile`}
      sx={{
        width,
        height,
        flex: "0 0 auto",
        border: "1px solid #DFE5EF",
        borderRadius: "50%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: "#1F4970",
        backgroundColor: "#F2F6FA",
        ...style,
      }}
      className={className}
    >
      <Box
        data-testid="service-user-pictogram"
        aria-hidden="true"
        sx={{ display: "grid", placeItems: "center" }}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width={Math.max(15, Math.round(width * 0.42))}
          height={Math.max(15, Math.round(height * 0.42))}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          style={{ display: "block", margin: 0 }}
        >
          <circle cx="12" cy="8" r="3.25" />
          <path d="M5.75 19c.65-3.2 2.75-5 6.25-5s5.6 1.8 6.25 5" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      </Box>
      {initials && (
        <Box
          data-testid="service-user-initials"
          aria-hidden="true"
          sx={{
            position: "static",
            color: "#1F4970",
            fontSize: Math.max(8, Math.round(width * 0.2)),
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {initials}
        </Box>
      )}
    </Box>
  );
};

export default CustomAvatar;
